import { useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import { createLien, validateCreateLien } from "../../../../lib/fetchApi";
import { notificationActions } from "../../../../store/notifications";
import Button from "../../../common/Button";
import Backdrop from "../../../common/modal/Backdrop";
import Modal from "../../../common/Modal";
import ModalInnerWrapper from "../../../common/ModalInnerWrapper";
import PannelBody from "../../../fragments/PannelBody";
import BodySimpleModalV3 from "../../../common/modal/BodySimpleModalV3";

function flattenErrorValues(value) {
    if (value == null) {
        return []
    }
    if (Array.isArray(value)) {
        return value.flatMap(flattenErrorValues)
    }
    if (typeof value === 'string') {
        const trimmed = value.trim()
        return trimmed ? [trimmed] : []
    }
    if (typeof value === 'object') {
        return Object.values(value).flatMap(flattenErrorValues)
    }
    return [`${value}`.trim()].filter(Boolean)
}

function collectValidationMessages(validate) {
    const merged = []

    if (validate && typeof validate === 'object') {
        const source = validate.data != null && typeof validate.data === 'object' ? validate.data : validate
        const candidates = [
            source.force_errors,
            source.forceErrors,
            source.validation_errors,
            source.validationErrors,
            source.errors,
            source.errorMessage,
            validate.errorMessage,
        ]
        merged.push(...candidates.flatMap(flattenErrorValues))
    }

    return [...new Set(merged.filter(Boolean))]
}

function shouldConfirmBeforeCreate(validate, messages) {
    if (messages.length > 0) {
        return true
    }
    if (validate?.is_valid === false) {
        return true
    }
    if (validate?.status === 422) {
        return true
    }
    return false
}

function isHardBlockMessage(message) {
    const text = String(message).toLowerCase()
    if (text.includes('do not lien')) {
        return true
    }
    // Missing source book/page cannot be overridden on Amend / Satisfy create.
    if (text.includes('must have book and page')) {
        return true
    }
    if (text.includes('must be recorded (book and page)')) {
        return true
    }
    return false
}

function isHardBlock(messages) {
    return messages.some((message) => isHardBlockMessage(message))
}

/**
 * Original lien (`type="lien"`): GA + different counties auto-files both sides at create.
 * Drawer "File missing county" is a fallback for errors and legacy single-side rows.
 */
export default function CreateLien({drawerData, btnText, type, validationSlug, permission, iconOnly = false}) {
    const dispatch = useDispatch()
    const userToken = useSelector(state => state.user.token)
    const activeCase = useSelector(state => state.case.activeCase) 

    const [showErrors, setShowErrors] = useState(null)
    const [showErrorsContinue, setShowErrorsContinue] = useState(null)
    const [loading, setLoading] = useState(false)
    const [createLienloading, setCreateLienloading] = useState(false)
    const doNotLienEngaged = Boolean(activeCase?.case?.do_not_lien)
    const blocked = permission || doNotLienEngaged

    async function submitCreate(body) {
        const update = await createLien(userToken, activeCase.case.id, body, dispatch)
        if (update && update.errorMessage) {
            dispatch(notificationActions.setError(update))
        }
    }

    async function createLienHandler() {
        setLoading(true)
        try {
            const reqBase = {
                case_practice_id: drawerData.id,
                type: type
            }

            const validate = await validateCreateLien(userToken, activeCase.case.id, drawerData.id, validationSlug)
            const messages = collectValidationMessages(validate)

            if (isHardBlock(messages)) {
                setShowErrors(messages)
            } else if (shouldConfirmBeforeCreate(validate, messages)) {
                setShowErrorsContinue(
                    messages.length > 0 ? messages : ['Validation reported issues. Continue anyway?']
                )
            } else {
                await submitCreate(reqBase)
            }
        } finally {
            setLoading(false)
        }
    }

    function closeShowErrorsHandler() {
        setShowErrors(null)
    }

    function closeShowErrorsContinueHandler() {
        setShowErrorsContinue(null)
    }

    async function continueWithErrorsHandler() {
        setCreateLienloading(true)
        try {
            await submitCreate({
                case_practice_id: drawerData.id,
                type: type,
                errors: showErrorsContinue
            })
            setShowErrorsContinue(null)
        } finally {
            setCreateLienloading(false)
        }
    }

    return <>
        {!blocked && (
            <Button btnStyle={'btn_small'} click={createLienHandler} isLoading={loading}>
                <i className="ti ti-square-plus"></i>
                {!iconOnly && btnText}
            </Button>
        )}
        {doNotLienEngaged && !permission && (
            <BodySimpleModalV3 message="Do Not Lien is engaged. Lien creation is blocked." widthAuto>
                <Button btnStyle={'btn_small'} isDisabled={true}>
                    <i className="ti ti-square-plus"></i>
                    {!iconOnly && btnText}
                </Button>
            </BodySimpleModalV3>
        )}

        {showErrors && <>
            {ReactDOM.createPortal(
                <Backdrop hide={closeShowErrorsHandler} />,
                document.getElementById("backdrop-root")
            )}  
            {ReactDOM.createPortal(
                <Modal>
                    <ModalInnerWrapper closeModal={closeShowErrorsHandler} modalTitle={`Lien creation blocked`}>                        
                        <PannelBody>
                            {showErrors.map(item => (
                                <p key={item}>{item}</p>
                            ))}                                                 
                        </PannelBody>
                    </ModalInnerWrapper>
                </Modal>,
                document.getElementById("overlay-root")
            )}          
        </>}

        {showErrorsContinue && <>
            {ReactDOM.createPortal(
                <Backdrop hide={closeShowErrorsContinueHandler} />,
                document.getElementById("backdrop-root")
            )}  
            {ReactDOM.createPortal(
                <Modal>
                    <ModalInnerWrapper closeModal={closeShowErrorsContinueHandler} modalTitle={`Lien creation warnings`}>                        
                        <PannelBody>
                            {showErrorsContinue.map(item => (
                                <p key={item}>{item}</p>
                            ))}     
                                                                      
                        </PannelBody>
                        <PannelBody>
                            <Button click={continueWithErrorsHandler} isLoading={createLienloading}>Continue with errors?</Button>  
                        </PannelBody>
                    </ModalInnerWrapper>
                </Modal>,
                document.getElementById("overlay-root")
            )}          
        </>}
    </>
}
